import * as apiErrorHelper from 'epic/apiErrorEpicHelper';
import { handleTradeAuthResult } from 'epic/workspace/ordersBasket/orderOperations/common/orderOperations.helper';
import { ofType } from 'redux-observable';
import type { Observable, SchedulerLike } from 'rxjs';
import { of, throwError } from 'rxjs';
import { catchError, filter, map, mergeMap, take, takeUntil } from 'rxjs/operators';
import type { Action } from 'state/actions';
import { ordersBasketFormTradeAuthFailed, ordersBasketFormTradeAuthTimeout } from 'state/orders-basket/actions';
import type { NextTradeAuthorizationResult } from 'state/quotes/trades/trades.model';
import type { EpicWithThunk } from 'state/state.common';
import { throwOnTimeout } from 'utils/rxjs';
import { fatEventApiConnector } from 'utils/streaming/api';
import { tradeAuthorization } from 'utils/streaming/messages';

export function quoteRemovedOrFailed(action$: Observable<Action>, formId: string) {
  return action$.pipe(
    ofType(
      'ORDERS_BASKET_FORM_DELETED',
      'ORDERS_BASKET_FORM_QUOTE_FAILED',
      'ORDERS_BASKET_FORM_QUOTE_TIMEOUT',
      'ORDERS_BASKET_FORM_QUOTE_CANCELED',
      'ORDERS_BASKET_FORM_QUOTE_REJECTED',
    ),
    filter(action => action.formId === formId),
  );
}

export function tradeAuthorizationForBookingEpic(appScheduler?: SchedulerLike): EpicWithThunk {
  return (action$, _state$, { oldHttpClient, frontLogger, streaming, getSgmeConfiguration }) =>
    action$.pipe(
      ofType('BOOKING_ACKED'),
      filter(action => action.workspace === 'ordersBasket'),
      mergeMap(({ formId, quoteId }) => {
        const quoteRemovedOrFailed$ = quoteRemovedOrFailed(action$, formId);
        const timeoutMs = getSgmeConfiguration().tradeAuthTimeoutMs;

        return tradeAuthorization(streaming).pipe(
          filter(tradeAuth => tradeAuth.quoteId === quoteId),
          mergeMap(lightEvent =>
            fatEventApiConnector(oldHttpClient)
              .getFatEvent<NextTradeAuthorizationResult>(lightEvent)
              .pipe(
                map(tradeAuth => handleTradeAuthResult(formId, tradeAuth, false)),
                catchError((error: Error) =>
                  throwError(new apiErrorHelper.EpicApiError(apiErrorHelper.defaultTechnicalErrorKey, error)),
                ),
              ),
          ),
          takeUntil(quoteRemovedOrFailed$),
          take(1),
          throwOnTimeout(
            timeoutMs,
            new apiErrorHelper.TimeoutError(timeoutMs, 'quote.errorMessages.tradeAuthorizationTimeout'),
            appScheduler,
          ),
          catchError((error: Error) => {
            frontLogger.error('An error occurred inside tradeAuthorizationEpic {@FrontError}', error);
            if (error.name === 'TimeoutError') {
              return of(ordersBasketFormTradeAuthTimeout(formId));
            }
            return of(ordersBasketFormTradeAuthFailed(formId, apiErrorHelper.toApiErrorMessage(error)));
          }),
        );
      }),
    );
}
